import {ConnectionSuggestionFeedbackDto, trackEvent, mapiqApi} from '@lib/infrastructure';
import {createAsyncThunk} from '@reduxjs/toolkit';
import parseISO from 'date-fns/parseISO';
import {normalize} from 'normalizr';
import {AppThunkApiConfig} from '../../createStore';
import {connectionEntity, ConnectionUser, ConnectionSuggestion} from '../entities';
import {toWorkspaceReservationsAndWorkdays} from './mappers/workspaceReservationMapper';
import {getConnectionById} from './selectors';
import {NormalizedConnectionsResult, WorkspaceReservationsWithWorkdays} from './types';

export const loadConnections = createAsyncThunk<NormalizedConnectionsResult, void, AppThunkApiConfig>(
  'connections/loadConnections',
  async (_arg, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);

    const connectionsResponse = await api.getConnections();

    const normalizedConnections = normalize<any, NormalizedConnectionsResult>(connectionsResponse, [connectionEntity]);

    return normalizedConnections.entities;
  },
);

export const loadWorkspaceReservationsForConnections = createAsyncThunk<
  WorkspaceReservationsWithWorkdays,
  {from: string; to: string},
  AppThunkApiConfig
>('connections/loadWorkspaceReservationsForConnections', async ({from: fromDateString, to: toDateString}, {extra}) => {
  const client = extra.apiClient;
  const api = mapiqApi(client);
  const from = parseISO(fromDateString);
  const to = parseISO(toDateString);

  const workspaceReservationsResponse = await api.getWorkspaceReservationsForConnections(from, to);
  return toWorkspaceReservationsAndWorkdays(workspaceReservationsResponse);
});

export const deleteConnection = createAsyncThunk<void, ConnectionUser, AppThunkApiConfig>(
  'connections/deleteConnection',
  async ({connectionId}, {extra, getState}) => {
    const client = extra.apiClient;
    const state = getState();
    const api = mapiqApi(client);
    await api.deleteConnection(connectionId as string);
    const connection = getConnectionById(state, connectionId as string);
    if (!connection) {
      return;
    }
    if (connection.status === 'Pending' && connection.direction === 'Outgoing') {
      trackEvent('RemoveConnectionRequest');
    } else {
      trackEvent('DeleteConnectionRequest');
    }
  },
);

export const acceptConnection = createAsyncThunk<void, ConnectionUser, AppThunkApiConfig>(
  'connections/acceptConnection',
  async ({connectionId}, {extra, dispatch}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    await api.patchConnection({id: connectionId, status: 'Accepted'});
    trackEvent('AcceptConnectionRequest');
  },
);

export const ignoreConnection = createAsyncThunk<void, ConnectionUser, AppThunkApiConfig>(
  'connections/ignoreConnection',
  async ({connectionId}, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    await api.patchConnection({id: connectionId, status: 'Ignored'});
    trackEvent('IgnoreConnectionRequest');
  },
);

export const sendConnection = createAsyncThunk<NormalizedConnectionsResult, ConnectionUser, AppThunkApiConfig>(
  'connections/sendConnection',
  async ({id}, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const connectionResponse = await api.postConnection(id);
    trackEvent('SendConnectionRequest');
    const normalizedConnection = normalize<any, NormalizedConnectionsResult>(connectionResponse, connectionEntity);
    return normalizedConnection.entities;
  },
);

export const searchConnections = createAsyncThunk<ConnectionUser[], string, AppThunkApiConfig>(
  'connections/searchUsers',
  async (searchTerm, {extra, getState}) => {
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch.length < 2) {
      return [];
    }

    const client = extra.apiClient;
    const actualState = getState();
    const {connectedUsers} = actualState.data.connections;
    const api = mapiqApi(client);
    const foundUsers = await api.getPeople(trimmedSearch);

    const searchResults = foundUsers.map(
      (user): ConnectionUser => ({
        ...user,
        connectionId: connectedUsers.entities[user.id]?.connectionId, // Links back to the connection if found
        isConnecting: false,
      }),
    );
    return searchResults;
  },
);

export const loadConnectionSuggestions = createAsyncThunk<ConnectionSuggestion, void, AppThunkApiConfig>(
  'connections/loadConnectionSuggestions',
  async (_arg, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const response = await api.getConnectionSuggestions();
    if (!response) {
      return {} as ConnectionSuggestion;
    }
    return response as ConnectionSuggestion;
  },
);

export const sendConnectionSuggestionFeedback = createAsyncThunk<
  void,
  ConnectionSuggestionFeedbackDto,
  AppThunkApiConfig
>('connections/sendConnectionSuggestionFeedback', async (feedback, {extra}) => {
  const client = extra.apiClient;
  const api = mapiqApi(client);
  await api.postConnectionSuggestionFeedback(feedback);
});
